import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { reducedMotion } from '../styles/motion';
// [ONE GLASS RECIPE 2026-09-01, user] The single source for the tooltip surface — see TipGlass.
import { GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassShadow } from './TipGlass';
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] popups placed in page px

/**
 * App-wide themed tooltip host.
 *
 * The browser draws its own plain rectangle for any element carrying a `title`
 * attribute — it ignores our theme and looks out of place. Rather than wrap every
 * call site, this single host (mounted once in <App/>) listens at the document
 * level: when the pointer rests on ANY element that has a real `title`, it
 *   1. stashes the text and STRIPS the attribute so the native box never shows,
 *   2. after a short hover delay, renders a glass capsule that matches the theme,
 *   3. restores the original `title` when the pointer leaves (so accessibility /
 *      React's DOM stay intact).
 *
 * Because it works off the rendered DOM, it automatically themes every `title`
 * in the app — buttons, table cells, badges, the mapping modal, etc. — and
 * ignores React props named `title` that never reach the DOM.
 */

// Hover dwell before the capsule appears — roughly the "leave it for a second"
// feel the user expects, without being sluggish.
const SHOW_DELAY_MS = 600;
const GAP = 8;        // px between the trigger and the capsule
const MARGIN = 8;     // keep this far from the viewport edges

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/**
 * Theme-aware glass capsule: light surface + dark ink in light mode, dark glass + light ink in
 * dark mode. Multi-line aware (pre-line preserves "\n"-joined titles) and wraps long single-line
 * values instead of running off-screen.
 *
 * [ONE GLASS RECIPE 2026-09-01, user] The surface is no longer written here — it comes from the
 * exported constants in TipGlass, the SAME ones the in-chart tooltips use, because the user asked
 * for one tooltip look "all over the application". This capsule had drifted to a near-opaque fill
 * (0.97/0.98) with a dimmer border and a 9px radius, which read as a different component beside a
 * chart tooltip; it is now the chart's translucent glass exactly.
 *
 * The ink stays defined here: the chart version paints its text as SVG <text> in colours the chart
 * owns, while this one carries plain prose and needs a high-contrast pair against the glass.
 */
const Box = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6000; /* above modals (1000/1100), drawers, toasts */
  pointer-events: none;
  max-width: 340px;
  white-space: pre-line;
  overflow-wrap: anywhere;
  text-align: left;

  padding: 7px 11px;
  border-radius: ${GLASS_RADIUS}px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: 0.01em;

  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#f1f5f9' : '#161917')};
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};

  animation: ${fadeIn} 140ms cubic-bezier(0.16, 1, 0.3, 1);
  ${reducedMotion} { animation: none; }
`;

interface Tip {
  text: string;
  anchor: DOMRect;
}

const GlobalTooltip: React.FC = () => {
  const [tip, setTip] = useState<Tip | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Position the capsule after it renders (pre-paint, so no flash): centre it
  // under the trigger, clamp inside the viewport, and flip above when the
  // bottom would be clipped.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!tip || !box) return;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom): the left / top set below are page px under the app zoom.
    const { width: bw, height: bh } = pageRect(box);
    const { width: vw, height: vh } = pageViewport();
    const anchor = pageRect(tip.anchor);

    let left = anchor.left + anchor.width / 2 - bw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));

    let top = anchor.bottom + GAP;
    if (top + bh > vh - MARGIN) {
      const above = anchor.top - GAP - bh;
      top = above >= MARGIN ? above : Math.max(MARGIN, Math.min(top, vh - bh - MARGIN));
    }

    box.style.left = `${Math.round(left)}px`;
    box.style.top = `${Math.round(top)}px`;
  }, [tip]);

  useEffect(() => {
    // [NESTED-TIP 2026-10-13, user] "Never two tooltips at once, and never the browser's rectangle."
    // While the pointer rests on a titled element, its title AND every titled ancestor's are held off
    // the DOM: with only its own gone, the browser falls back to the nearest ancestor's title and draws
    // THAT as its rectangle (the red Sales Pipeline card's text did, over its Declined button).
    let pointerEl: HTMLElement | null = null; // nearest titled element under the pointer
    let held: HTMLElement[] = [];             // pointerEl + its titled ancestors, titles in __ttTitle
    let tipEl: HTMLElement | null = null;     // whose text the capsule shows / is about to show
    let owned = false;                        // the pointer is on one of the app's own tooltips (data-own-tip)
    let timer: number | null = null;

    const textOf = (el: HTMLElement): string | null => {
      if (!held.includes(el)) return el.getAttribute('title');
      const s = (el as any).__ttTitle;
      return typeof s === 'string' ? s : null;
    };

    const hideTip = () => {
      if (timer != null) { window.clearTimeout(timer); timer = null; }
      tipEl = null;
      setTip(null);
    };

    const showSoon = (el: HTMLElement) => {
      hideTip();
      tipEl = el;
      timer = window.setTimeout(() => {
        timer = null;
        const text = tipEl ? textOf(tipEl) : null;
        if (!tipEl || owned || !text || !text.trim()) return;
        setTip({ text, anchor: tipEl.getBoundingClientRect() });
      }, SHOW_DELAY_MS);
    };

    // React writes a changed title prop straight back onto the DOM (the Decline button becomes
    // "Declined. Click to undo" on the click). On a held element that would bring the rectangle back
    // and later lose to the old text on leave: keep the new text, take it off again, update the capsule.
    const onTitleWrite = (records: MutationRecord[]) => {
      for (const r of records) {
        const el = r.target as HTMLElement;
        const next = el.getAttribute('title');
        if (next == null || !held.includes(el)) continue; // our own removal
        (el as any).__ttTitle = next;
        el.removeAttribute('title');
        if (el !== tipEl) continue;
        if (!next.trim()) hideTip();
        else setTip((cur) => (cur ? { text: next, anchor: el.getBoundingClientRect() } : cur));
      }
    };
    const watch = typeof MutationObserver === 'function' ? new MutationObserver(onTitleWrite) : null;

    const hold = (el: HTMLElement) => {
      for (let n: HTMLElement | null = el; n; n = n.parentElement) {
        const t = n.getAttribute('title');
        if (t == null) continue;
        (n as any).__ttTitle = t;
        n.removeAttribute('title');
        held.push(n);
        watch?.observe(n, { attributes: true, attributeFilter: ['title'] });
      }
      pointerEl = el;
    };

    // Put the stashed titles back (an element may have been removed — guard it).
    const release = () => {
      if (pointerEl && tipEl === pointerEl) hideTip();
      if (watch) { onTitleWrite(watch.takeRecords()); watch.disconnect(); }
      for (const n of held) {
        const stashed = (n as any).__ttTitle;
        delete (n as any).__ttTitle;
        if (stashed != null) {
          try { n.setAttribute('title', stashed); } catch { /* detached node */ }
        }
      }
      held = [];
      pointerEl = null;
    };

    const onOver = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || typeof t.closest !== 'function') return;
      owned = !!t.closest('[data-own-tip]');
      if (owned) hideTip(); // one of the app's own tooltips covers this spot: it is the only one
      if (pointerEl && !pointerEl.isConnected) release(); // removed while hovered: no pointerout comes
      const cand = t.closest('[title]') as HTMLElement | null;
      // Still inside the held element: only an element INSIDE it with its own title takes over (the
      // Declined button on a red card) — it used to be skipped, so the browser drew its rectangle.
      if (pointerEl && pointerEl.contains(t) && !(cand && pointerEl.contains(cand))) return;
      if (pointerEl) release();
      if (!cand || cand.getAttribute('title') === '') return; // an empty title shows nothing anywhere
      hold(cand);
      if (!owned) showSoon(cand);
    };

    const onOut = (e: PointerEvent) => {
      if (!pointerEl) return;
      const related = e.relatedTarget as Node | null;
      if (related && pointerEl.contains(related)) return; // moved within the trigger
      release();
    };

    // [PHONE-LAYOUT 2026-10-22] Phones: a tap is a touch press, then the pointer LEAVES, and only then does the
    // browser focus the tapped button (Android) — so the focus below used to open the tooltip 0.6 s after a tap,
    // and it stayed on screen (a "Close" capsule left floating after closing the Bin). On a phone a focus that
    // comes right after a finger press is that tap, not the keyboard: no capsule. Mouse and keyboard unchanged.
    let touchAt = 0;
    const notePress = (e: PointerEvent) => { if (e.pointerType !== 'mouse') touchAt = Date.now(); };
    const tapFocus = () => Date.now() - touchAt < 900
      && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches;

    // Keyboard parity — show on focus, hide on blur. The focus a press gives the pressed element is
    // skipped: the press itself just hid the capsule.
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (tapFocus()) return; // [PHONE-LAYOUT 2026-10-22] the focus of a finger tap on a phone
      if (!t || typeof t.closest !== 'function' || (pointerEl && pointerEl.contains(t))) return;
      if (t.closest('[data-own-tip]')) return;
      const cand = t.closest('[title]') as HTMLElement | null;
      if (cand && cand !== tipEl && (cand.getAttribute('title') || '').trim()) showSoon(cand);
    };
    const onFocusOut = () => { if (tipEl && tipEl !== pointerEl) hideTip(); };

    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerout', onOut, true);
    // A press, a scroll or a window switch only HIDES the capsule. The titles stay held while the
    // pointer is still there: putting them back on the press let the browser draw its rectangle right
    // after the click.
    document.addEventListener('pointerdown', hideTip, true);
    // [PHONE-LAYOUT 2026-10-22] when the last finger (not mouse) press began and ended — see tapFocus
    document.addEventListener('pointerdown', notePress, true);
    document.addEventListener('pointerup', notePress, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('scroll', hideTip, true);
    window.addEventListener('resize', hideTip);
    window.addEventListener('blur', hideTip);

    return () => {
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerout', onOut, true);
      document.removeEventListener('pointerdown', hideTip, true);
      document.removeEventListener('pointerdown', notePress, true); // [PHONE-LAYOUT 2026-10-22]
      document.removeEventListener('pointerup', notePress, true); // [PHONE-LAYOUT 2026-10-22]
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      window.removeEventListener('scroll', hideTip, true);
      window.removeEventListener('resize', hideTip);
      window.removeEventListener('blur', hideTip);
      release();
      hideTip();
    };
  }, []);

  if (!tip) return null;
  return createPortal(<Box ref={boxRef}>{tip.text}</Box>, document.body);
};

export default GlobalTooltip;
